import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { PurchaseResponse } from '@app/models/purchase-response.model'
import { QuoteSearchResponse } from '@app/models/quote-search-response.model'
import { QuoteSession } from '@app/models/quote-session.model'
import { getEndpoint } from '@app/utils/get-endpoint'
import { catchError, map, Observable, throwError } from 'rxjs'

@Injectable({
  providedIn: 'root',
})
export class QuoteSessionService {
  #service: string = 'QuoteSession'

  constructor(private readonly http: HttpClient) { }

  /** /api/v1/QuoteSession/GenerateGuid */
  public generateGuid(): Observable<string> {
    return this.http
      .get(getEndpoint([this.#service, 'GenerateGuid']), { responseType: 'text' })
      .pipe(
        map((res) => res.replace(/"/g, '')),
        catchError((err) => {
          let errorMessage: string = err.error ? JSON.parse(err.error) : 'There was an error'

          return throwError(() => errorMessage)
        })
      )
  }

  /** /api/v1/QuoteSession/CreateNewEmptySession */
  public createNewEmptySession(): Observable<QuoteSession> {
    return this.http.get<QuoteSession>(getEndpoint([this.#service, 'CreateNewEmptySession']))
  }

  public retrieveSessionBySearchResult(req: QuoteSearchResponse): Observable<QuoteSession> {
    return this.http.post<QuoteSession>(
      getEndpoint([this.#service, 'RetrieveSessionBySearchResult']),
      req
    )
  }

  /**
   * @deprecated Use @method saveSession instead
   */
  public upsertQuoteSession(session: QuoteSession): Observable<QuoteSession> {
    return this.http.post<QuoteSession>(getEndpoint([this.#service, 'UpsertQuoteSession']), session)
  }

  /**
   * Saves the session and recalculates if there is a Quote ID
   * /api/v1/QuoteSession/SaveRecalculateQuoteSession
   */
  public saveSession(req: QuoteSession): Observable<QuoteSession> {
    return this.http.post<QuoteSession>(
      getEndpoint([this.#service, 'SaveRecalculateQuoteSession']),
      req
    )
  }

  /**
   * Clears a referral account from a quote based on the quoteId
   * /api/v1/QuoteSession/ClearReferredAccount
   */
  public clearReferredAccount(quoteSession: QuoteSession): Observable<QuoteSession> {
    return this.http.post<QuoteSession>(
      getEndpoint([this.#service, 'ClearReferredAccount']),
      quoteSession
    )
  }

  /**
   * Save while allowing some partial values to be saved
   * where they would otherwise throw an error
   * /api/v1/QuoteSession/SaveAndQuit
   */
  public saveForClose(req: QuoteSession): Observable<unknown> {
    return this.http.post<unknown>(getEndpoint([this.#service, 'SaveAndQuit']), req)
  }

  /** /api/v1/QuoteSession/EmailQuote */
  public emailQuote(req: QuoteSession): Observable<QuoteSession> {
    return this.http.post<QuoteSession>(getEndpoint([this.#service, 'EmailQuote']), req)
  }

  /** /api/v1/Payment/PurchasePolicy */
  public purchasePolicy(req: QuoteSession): Observable<PurchaseResponse> {
    return this.http.post<PurchaseResponse>(getEndpoint(['Payment', 'PurchasePolicy']), req)
  }

  /** /api/v1/Quote/{QuoteID}/checkout */
  public checkout(quoteId: string, req: QuoteSession): Observable<any> {
    return this.http.post<any>(getEndpoint(['Checkout', quoteId,]), req)
  }

  /** /api/v1/Purchase */
  public purchase(req: any): Observable<QuoteSession> {
    return this.http.post<QuoteSession>(getEndpoint(['Purchase']), req)
  }
}
